// oxlint-disable typescript/no-unsafe-type-assertion

import { setup } from 'xstate5';

import { Actions, Actors, createInitialContext, Guards } from './setup';
import { type Context, type Event, type Input } from './types';

// ─────────────────────────────────────────────────────────────────────────────

export const scrollspyMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Event,
    input: {} as Input,
  },
  actions: {
    updateActiveTarget: Actions.updateActiveTarget,
    setActiveTarget: Actions.setActiveTarget,
    scrollToTarget: Actions.scrollToTarget,
    scrollToDefaultTarget: Actions.scrollToDefaultTarget,
    reScrollToActiveTarget: Actions.reScrollToActiveTarget,
    clearProgrammaticTarget: Actions.clearProgrammaticTarget,
    storeMeasurement: Actions.storeMeasurement,
    removeMeasurement: Actions.removeMeasurement,
    storeContentHeight: Actions.storeContentHeight,
    storeViewportHeight: Actions.storeViewportHeight,
    storeAllMeasurements: Actions.storeAllMeasurements,
    clearDefaultTarget: Actions.clearDefaultTarget,
  },
  actors: {
    measureAllTargets: Actors.measureAllTargets,
  },
  guards: {
    checkIfReachedTarget: Guards.checkIfReachedTarget,
    checkIfHasMeasurements: Guards.checkIfHasMeasurements,
  },
  delays: {
    SCROLL_DEBOUNCE: 100,
    PROGRAMMATIC_SCROLL_TIMEOUT: 2000,
    REMEASURE_DEBOUNCE: 200,
  },
}).createMachine({
  id: 'scroll-spy',
  context: createInitialContext,
  initial: 'idle',
  states: {
    idle: {
      id: 'idle',
      description: 'Waiting for scroll or navigation events.',
      on: {
        scroll: { target: 'scroll-debouncing' },
      },
    },
    'scroll-debouncing': {
      id: 'scroll-debouncing',
      description: 'Debouncing scroll events. Active target updates once scrolling stops.',
      on: {
        scroll: { target: 'scroll-debouncing' },
      },
      after: {
        SCROLL_DEBOUNCE: {
          target: '#idle',
          actions: ['updateActiveTarget'],
        },
      },
    },
    'scrolling-to-target': {
      id: 'scrolling-to-target',
      description:
        'Animated scroll to a tapped target. Active target is locked until destination is reached.',
      on: {
        scroll: {
          target: '#scroll-settling',
          guard: 'checkIfReachedTarget',
          actions: ['clearProgrammaticTarget'],
        },
      },
      after: {
        PROGRAMMATIC_SCROLL_TIMEOUT: {
          target: '#idle',
          actions: ['clearProgrammaticTarget'],
        },
      },
    },
    'scroll-settling': {
      id: 'scroll-settling',
      description: 'Brief grace period after reaching target. Absorbs trailing scroll events.',
      after: {
        SCROLL_DEBOUNCE: { target: '#idle' },
      },
    },
    'pending-remeasurement': {
      id: 'pending-remeasurement',
      description: 'Debouncing content size changes before re-measuring all targets.',
      on: {
        'scrollView.contentSizeChange': {
          target: 'pending-remeasurement',
          actions: ['storeContentHeight'],
        },
      },
      after: {
        REMEASURE_DEBOUNCE: { target: '#remeasuring' },
      },
    },
    remeasuring: {
      id: 'remeasuring',
      description: 'Measuring all target positions. Scrolls to active target if it shifted.',
      invoke: {
        src: 'measureAllTargets',
        input: ({ context }) => ({ context }),
        onDone: {
          target: '#idle',
          actions: [
            {
              type: 'storeAllMeasurements',
              params: ({ event }) => ({ measurements: event.output }),
            },
            'reScrollToActiveTarget',
          ],
        },
        onError: { target: '#idle' },
      },
    },
  },
  on: {
    'target.scrollTo': {
      target: '#scrolling-to-target',
      actions: ['setActiveTarget', 'scrollToTarget'],
    },
    'target.measure': { actions: ['storeMeasurement'] },
    'target.deregister': { actions: ['removeMeasurement'] },
    'scrollView.contentSizeChange': [
      {
        target: '#pending-remeasurement',
        guard: 'checkIfHasMeasurements',
        actions: ['storeContentHeight'],
      },
      { actions: ['storeContentHeight'] },
    ],
    'scrollView.layout': { actions: ['storeViewportHeight'] },
    'defaultTarget.scrollTo': {
      actions: ['scrollToDefaultTarget', 'clearDefaultTarget'],
    },
  },
});
